import { css } from '@linaria/core';
import { getCodeSnippet, ModelLanguage } from 'modules/EntityReference/CodeSnippets';
import { FC } from 'react';
import { useField, useFormikContext } from 'formik';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { Select } from 'components/Select';
import { FormatOptionForPAT } from 'modules/Home/components/FormatOptionForPAT';
import { usePATOptions } from 'modules/Home/components/usePATOptions';
import { useCurrentUserId, useIsInternalUser } from 'modules/Auth/hooks';
import { checkIfPATHasPredictAccess } from 'modules/EntityReference/TabAPI';
import { useMstInstanceCreator } from 'utils/mst/useMstInstanceCreator';
import { createAppTokensStore, KeyCreationEventType } from 'modules/Apps/mstStores/UserKeysMST';
import { useAddApiKeyModal } from 'modules/Apps/components/apiKeyTable/apiKeyModalHooks';
import { PATKeyType } from 'modules/AppSettings/ApiKeysTable/constants';
import { useLanguageOptionsForModels } from 'modules/EntityReference/CodeSnippets/useLanguageOptionsForModels';
import { Selector } from './Selector';
import SvgCopy02Sm from './Icons/Copy02Sm';
import { PlaygroundConfig } from '../types/playgroundConfig';
import { getModelSignaturesFromModelVersion } from '../hooks/utils/useFetchModelOutput/getModelSignaturesFromModelVersion';
import { obfuscateToken } from '../utils/obfuscateToken';
const LanguageMap: Record<ModelLanguage, string> = {
  [ModelLanguage.Python]: 'python',
  [ModelLanguage.PythonSDK]: 'python',
  [ModelLanguage.PythonMCP]: 'python',
  [ModelLanguage.PythonOpenAI]: 'python',
  [ModelLanguage.Javascript]: 'javascript',
  [ModelLanguage.NodeJS]: 'javascript',
  [ModelLanguage.NodeJS_SDK]: 'javascript',
  [ModelLanguage.NodeJSVercel]: 'javascript',
  [ModelLanguage.NodeJSOpenAI]: 'javascript',
  [ModelLanguage.cURL]: 'bash',
  [ModelLanguage.Java]: 'java',
  [ModelLanguage.PHP]: 'php'
};
export const ApiUI: FC = () => {
  const {
    track
  } = useDataCollection();
  const userId = useCurrentUserId();
  const store = useMstInstanceCreator(createAppTokensStore, []);
  const [{
    value: model
  }] = useField<PlaygroundConfig['model']>('model');
  const [{
    value: deploymentId
  }] = useField<PlaygroundConfig['deploymentId']>('deploymentId');
  const {
    values
  } = useFormikContext<PlaygroundConfig>();
  const method_signatures = getModelSignaturesFromModelVersion(values.modelVersion, values.model);
  const {
    languageOptions,
    selectedLanguage,
    setSelectedLanguage
  } = useLanguageOptionsForModels({
    modelMethodSignatures: method_signatures,
    isInternalUser: useIsInternalUser()
  });
  const handleLanguageChange = (newLang: string) => {
    setSelectedLanguage((newLang as ModelLanguage));
  };
  const onPatCreated = (e: KeyCreationEventType) => {
    if (e === KeyCreationEventType.SUCCESS) {
      setSelectedPatOption(store.apiKeys[0].id);
    }
  };
  const openAddApiKeyModal = useAddApiKeyModal(store, {
    type: PATKeyType,
    userId: (userId as string)
  }, onPatCreated);
  const {
    patOptions,
    selectedPATOption,
    setSelectedPatOption,
    isLoading: isPATLoading
  } = usePATOptions({
    userId: (userId as string),
    filterPAT: checkIfPATHasPredictAccess(true),
    onCreateNewPATClick: openAddApiKeyModal
  });
  const codeSnippet = model ? getCodeSnippet({
    personalAccessToken: selectedPATOption,
    language: selectedLanguage,
    entity: ((model as unknown) as CF.API.Models.Model),
    silent: true,
    deploymentId,
    method_signatures
  }) : '';
  const onClickCopy = () => {
    navigator.clipboard.writeText(codeSnippet);
    cogoToast.success('Code copied to clipboard');
    track('Playground -> Code Copied', {
      language: selectedLanguage,
      modelId: model?.id
    });
  };
  return <div className={ApiContainer}>
      <div className={ApiHeader}>
        <div>
          <Selector options={[{
          title: 'Language',
          optionsList: languageOptions
        }]} value={selectedLanguage} onChange={handleLanguageChange} />
        </div>
        {!!userId && <Select modal title={<FormatOptionForPAT pat={selectedPATOption ?? ''} isLoading={isPATLoading} />} onChange={({
        value
      }) => setSelectedPatOption(value)} options={patOptions} className={PATSelector} />}
        <button type="button" className={CopyButton} onClick={onClickCopy}>
          <SvgCopy02Sm />
          Copy
        </button>
      </div>
      <div className={ApiCodeBlock}>
        <SyntaxHighlighter language={LanguageMap[selectedLanguage] ?? selectedLanguage.toLowerCase()} style={dracula} className={codeBlock} wrapLongLines>
          {obfuscateToken(codeSnippet, selectedPATOption)}
        </SyntaxHighlighter>
      </div>
    </div>;
};
const PATSelector = "odules__Playground__Components__ApiUI_tsx-PATSelector-pbqsxcm";
const ApiContainer = "odules__Playground__Components__ApiUI_tsx-ApiContainer-a1jkvmo6";
const ApiHeader = "odules__Playground__Components__ApiUI_tsx-ApiHeader-ar8wt6u";
const CopyButton = "odules__Playground__Components__ApiUI_tsx-CopyButton-c1qnj0fg";
const ApiCodeBlock = "odules__Playground__Components__ApiUI_tsx-ApiCodeBlock-avlrlt0";
const codeBlock = "odules__Playground__Components__ApiUI_tsx-codeBlock-c1kc4iwe";

require("./ApiUI.linaria.module.css!=!../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./ApiUI.tsx");